/**
 * Money helpers. The API returns money as JSON NUMBERS with 2 decimals (DECIMAL(12,2) cast to float),
 * but we also accept numeric strings ("1234.50") defensively, e.g. from inputs or a future API change.
 *
 * Rule: never add/multiply raw floats. Convert to integer cents with `toCents`, do the arithmetic on
 * cents, and convert back with `fromCents`. Rounding is half-up (matches the backend's round2).
 */

export type MoneyInput = number | string | null | undefined;

export const DEFAULT_CURRENCY = 'LKR';

let activeCurrency = DEFAULT_CURRENCY;

/** Server `currency` setting; set whenever settings load/update (lib/api/settings.ts, useSettings). formatMoney uses it by default. */
export function setActiveCurrency(code: string | null | undefined): void {
  const c = (code ?? '').trim();
  activeCurrency = c || DEFAULT_CURRENCY;
}

export function getActiveCurrency(): string {
  return activeCurrency;
}

/** Parse any money-ish value to integer cents. Invalid/empty -> 0. Half-up rounding, sign-aware. */
export function toCents(value: MoneyInput): number {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'string') {
    const s = value.trim().replace(/,/g, '');
    const m = /^(-)?(\d*)(?:\.(\d*))?$/.exec(s);
    if (!m || (m[2] === '' && (m[3] ?? '') === '')) return 0;
    const neg = m[1] === '-';
    const whole = Number(m[2] || '0');
    const frac = (m[3] ?? '').padEnd(3, '0'); // need the 3rd digit to round half-up
    let cents = whole * 100 + Number(frac.slice(0, 2));
    if (Number(frac[2]) >= 5) cents += 1;
    return neg ? -cents : cents;
  }
  if (!Number.isFinite(value)) return 0;
  // Math.round on |x| gives half-up away from zero; EPSILON nudge fixes 1.005 style representation errors.
  const abs = Math.round((Math.abs(value) + Number.EPSILON) * 100);
  return value < 0 ? -abs : abs;
}

export function fromCents(cents: number): number {
  return Math.round(cents) / 100;
}

/** Round to 2 decimals half-up. */
export function round2(value: MoneyInput): number {
  return fromCents(toCents(value));
}

export function addMoney(...values: MoneyInput[]): number {
  return fromCents(values.reduce<number>((sum, v) => sum + toCents(v), 0));
}

export function subMoney(a: MoneyInput, b: MoneyInput): number {
  return fromCents(toCents(a) - toCents(b));
}

/** price * quantity (quantity may have up to 3 decimals), rounded half-up to cents. */
export function mulMoney(price: MoneyInput, qty: number): number {
  const raw = toCents(price) * qty;
  const rounded = Math.round(Math.abs(raw) + 1e-9); // half-up away from zero
  return fromCents(raw < 0 ? -rounded : rounded);
}

function groupThousands(intPart: string): string {
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** 123450 -> "1,234.50" (no currency). Exact: no float arithmetic on the formatted digits. */
export function formatAmount(value: MoneyInput, opts: { decimals?: 0 | 2 } = {}): string {
  const cents = toCents(value);
  const abs = Math.abs(cents);
  const whole = Math.floor(abs / 100);
  const frac = String(abs % 100).padStart(2, '0');
  const body =
    opts.decimals === 0
      ? groupThousands(String(Math.round(abs / 100)))
      : `${groupThousands(String(whole))}.${frac}`;
  return cents < 0 ? `-${body}` : body;
}

/** "LKR 1,234.50" (currency = server setting by default). Negative: "-LKR 1,234.50". Null/undefined/invalid -> "LKR 0.00". */
export function formatMoney(
  value: MoneyInput,
  opts: { currency?: string; decimals?: 0 | 2 } = {},
): string {
  const currency = opts.currency ?? activeCurrency;
  const amount = formatAmount(value, opts);
  return amount.startsWith('-') ? `-${currency} ${amount.slice(1)}` : `${currency} ${amount}`;
}

/** Parse user input ("1,234.5", " 12 ") to a number with 2dp, or null when empty/invalid. */
export function parseMoneyInput(raw: string): number | null {
  const s = raw.trim().replace(/,/g, '');
  if (s === '' || !/^-?\d*\.?\d*$/.test(s) || s === '-' || s === '.') return null;
  return fromCents(toCents(s));
}
